import renderAmountField from 'components/FormControls/amountField'
import renderSelect from 'components/FormControls/select'
import renderInputField from 'components/FormControls/textInput'
import GridSpinner from 'components/UI/GridSpinner'
import { Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import emailValidator from 'utils/validators/emailValidator'
import * as Yup from 'yup'
import SERVICES_API_V1 from 'api/services/v1'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import FormFrame from 'modules/Forms/Account/Create/subcomponents/FormFrame'
import clsx from 'clsx'
import formStyles from 'modules/Forms/common-form.module.scss'

export const TaboolaSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    network: Yup.string().required('common::validation_messages.required'),
    industry: Yup.string().required('common::validation_messages.required'),
    email: Yup.string()
      .test('emailValidator', 'common::validation_messages.incorrect_email_format', emailValidator)
      .required('common::validation_messages.required'),
    site: Yup.string().required('common::validation_messages.required'),
    budget: Yup.string().required('common::validation_messages.required'),
  })

let CreateTaboolaAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkId,
  form,
  initialValues,
  array,
  multiLogins,
  networkType,
}) => {
  const selector = formValueSelector(form)
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })

  const [industriesLoading, setIndustriesLoading] = useState(true)
  const [industriesList, setIndustriesList] = useState([])

  const { email, user } = useSelector((state) => ({
    email: selector(state, 'email'),
    user: state['app'].user,
  }))

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  useEffect(() => {
    if (!networkId) {
      return
    }

    if (industriesList.length === 0) {
      SERVICES_API_V1.getNetworkSettings({ networkId }).then(({ response: { data } }) => {
        setIndustriesList(
          Object.keys(data?.industries || {}).map((key) => {
            return {
              label: t(`industries::${key}`),
              items: data.industries[key].map((key) => ({
                value: key,
                label: t(`industries::${key}`),
              })),
            }
          }),
        )
        setIndustriesLoading(false)
      })
    } else {
      setIndustriesLoading(false)
    }
  }, [setIndustriesList, setIndustriesLoading, industriesList, networkId])

  return (
    <FormFrame
      onSubmit={handleSubmit(submitAccountData)}
      multiLogins={multiLogins}
      form={form}
      submitAccountData={submitAccountData}
      submitting={submitting}
      onClose={onClose}
    >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.taboola.fields.login.label'}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t('services::forms.register_account.taboola.fields.industry.label')}
            name="industry"
            component={renderSelect}
            fullWidth
            t={t}
            normalize={(value) => value ?? ''}
            emptyOptionLabel={t('common::select_empty_option')}
            clearOptionLabel={t('common::select_clear_option')}
            options={industriesList}
            readOnly={industriesLoading}
            ControlProps={{
              startAdornment: industriesLoading && <GridSpinner width={24} />,
            }}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="email"
            type="email"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.taboola.fields.email.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="site"
            type="url"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.taboola.fields.site.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            label={t('services::forms.register_account.taboola.fields.budget.label')}
            name="budget"
            type="text"
            component={renderAmountField}
            t={t}
            fullWidth
            autocomplete="off"
            format={(value) => (value === null ? '' : '' + value)}
            parse={(value) => value.replace(/\s/g, '').replace(',', '.')}
            InputProps={{
              endAdornment: (
                <Typography
                  variant={'Body-25'}
                  noMargin
                  color={'Label-Secondary'}
                >
                  RUB
                </Typography>
              ),
            }}
          />
        </div>

    </FormFrame>
  )
}

CreateTaboolaAccountForm = reduxForm({
  form: 'CreateTaboolaAccountForm',
  initialValues: {
    network: '',
    login: '',
    industry: '',
    email: '',
    site: '',
    budget: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateTaboolaAccountForm)

CreateTaboolaAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  networkType: PropTypes.string,
}

export default CreateTaboolaAccountForm
